import React, { useEffect } from "react";
import PropTypes from "prop-types";
// @mui
import {
  Stack,
  Drawer,
  Divider,
  Checkbox,
  Typography,
  FormControlLabel,
  TextField,
  Box,
  IconButton,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";

// components
import { Close } from "@mui/icons-material";
import {
  useCreateSupportedLanguageMutation,
  useUpdateSupportedLanguageMutation,
} from "../../../redux/services/supportedLanguages.service";
import Scrollbar from "../../../components/scrollbar";
import { SupportedLanguagesSchema } from "../../../forms-schema/form.schema";

SupportedLanguagesForm.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  edit: PropTypes.bool,
  openRow: PropTypes.object,
};

export default function SupportedLanguagesForm({
  open,
  onClose,
  edit,
  openRow,
}) {
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(SupportedLanguagesSchema),
  });

  const [createSupportedLanguages, { isLoading }] =
    useCreateSupportedLanguageMutation();
  const [updateSupportedLanguages, { isLoading: isUpdateLoading }] =
    useUpdateSupportedLanguageMutation();

  const handleClick = async (values) => {
    let resultAction;
    if (edit) {
      resultAction = await updateSupportedLanguages({
        ...values,
        id: openRow.id,
      });
    } else {
      resultAction = await createSupportedLanguages({ ...values });
    }
    if (resultAction?.data?.success) {
      onClose();
      reset();
    }
  };

  useEffect(() => {
    if (edit) {
      setValue("languageName", openRow.languageName);
      setValue("languageCode", openRow.languageCode);
      setValue("isOffline", openRow.isOffline);
    } else {
      reset();
    }
  }, [edit, open, openRow, setValue, reset]);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: 400, border: "none", overflow: "hidden" },
        }}
      >
        <Scrollbar>
          <Stack spacing={3} sx={{ p: 3 }}>
            <div>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.5,
                }}
              >
                <Typography variant="subtitle1">
                  {edit ? "Edit" : "Create"} Supported Language
                </Typography>
                <IconButton onClick={onClose}>
                  <Close />
                </IconButton>
              </Box>
              <Divider />
            </div>
            <form onSubmit={handleSubmit(handleClick)}>
              <Stack spacing={3}>
                <TextField
                  name="languageName"
                  label="Language Name"
                  {...register("languageName")}
                  error={Boolean(errors.languageName)}
                  helperText={errors.languageName?.message}
                  inputProps={{ "data-testid": "languageName" }}
                />

                <TextField
                  name="languageCode"
                  label="Language Code"
                  {...register("languageCode")}
                  error={Boolean(errors.languageCode)}
                  helperText={errors.languageCode?.message}
                  inputProps={{ "data-testid": "languageCode" }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      defaultChecked={edit && openRow?.isOffline}
                      checked={watch("isOffline")}
                      {...register("isOffline")}
                      inputProps={{ "data-testid": "isOffline" }}
                    />
                  }
                  label="Is Offline"
                />

                <LoadingButton
                  loading={edit ? isUpdateLoading : isLoading}
                  type="submit"
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                >
                  Submit
                </LoadingButton>
              </Stack>
            </form>
          </Stack>
        </Scrollbar>
      </Drawer>
    </>
  );
}
